'use client';

/**
 * useUnifiedAdd — state machine for the Unified Add Sheet.
 *
 * Responsibilities:
 *  - Manage the staged "pending items" queue (not yet persisted)
 *  - Track the target collection (existing | new | none)
 *  - Drive the submit flow (3 paths: new list / existing list / library-only)
 *  - Expose a derived `ctaLabel` so the sheet button always matches state
 *
 * The hook is intentionally self-contained: all API calls live here, keeping
 * the sheet component purely presentational.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { createCollection, createItem, addCollectionItem, fetchCircles } from '@/lib/api';
import { PROVIDER_CONFIG } from '@/lib/category-config';
import type { ProviderValue } from '@/components/quick-add/category-chips';
import type { SearchResult } from '@/lib/api';

// ── localStorage key for recently used list ───────────────────────────────────
const LAST_LIST_KEY = 'favely:last-used-list';

// ── sessionStorage key for draft pending items ────────────────────────────────
const DRAFT_KEY = 'favely:quick-add-draft';

interface DraftState {
  pendingItems: PendingItem[];
  target: CollectionTarget;
  listInput: string;
}

function readDraft(): DraftState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftState;
    if (!Array.isArray(parsed?.pendingItems)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDraft(state: DraftState): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable — degrade gracefully
  }
}

function clearDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

/** Read the last-used collection target from localStorage, or null. */
function readLastUsedList(): CollectionTarget | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LAST_LIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?._id && parsed?.title) return parsed as CollectionTarget;
    return null;
  } catch {
    return null;
  }
}

/** Persist the current target to localStorage so the next open pre-fills it. */
function writeLastUsedList(target: CollectionTarget): void {
  if (typeof window === 'undefined') return;
  try {
    if (target && target._id !== 'NEW') {
      localStorage.setItem(LAST_LIST_KEY, JSON.stringify(target));
    }
  } catch {
    // localStorage unavailable — degrade gracefully
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

/** An item that the user has staged but not yet saved. */
export interface PendingItem {
  /** Unique key within the queue. externalId for enriched items; a UUID for manual ones. */
  key: string;
  title: string;
  thumbnail?: string;
  /** The resolved MongoDB category ObjectId for this item. */
  categoryId: string;
  /** Human-readable label shown in the pending queue chip. */
  categoryLabel: string;
  /** Full enrichment payload — null for manual (Other) items. */
  searchResult: SearchResult | null;
  /** API provider slug (e.g. 'tmdb', 'open_library') — null for manual items. */
  apiProvider: string | null;
  /** For TMDB items: 'movie' | 'tv' */
  mediaType?: 'movie' | 'tv';
}

/**
 * The collection the user wants to save to.
 *   - { _id: string, title: string }        → existing collection
 *   - { _id: 'NEW', title: string }          → create a new one on submit
 *   - null                                   → library only (no collection link)
 */
export type CollectionTarget =
  | { _id: string; title: string }
  | { _id: 'NEW'; title: string }
  | null;

// ── Hook ──────────────────────────────────────────────────────────────────────

interface UseUnifiedAddReturn {
  // ── State ────────────────────────────────────────────────
  pendingItems: PendingItem[];
  target: CollectionTarget;
  selectedProvider: ProviderValue;
  listInput: string;
  isSubmitting: boolean;
  /**
   * A3 — After a successful submit, holds the destination name so the sheet
   * can show "Added to X — add to another list?" without clearing items.
   * Null when no submit has happened yet in this session.
   */
  lastSubmitDestination: string | null;

  // ── Actions ──────────────────────────────────────────────
  /** Stage an item in the pending queue. No-op if already staged (by key). */
  addPendingItem: (item: PendingItem) => void;
  /** Remove an item from the queue by its key. */
  removePendingItem: (key: string) => void;
  /** Set the collection target (existing, NEW, or null). */
  setTarget: (target: CollectionTarget) => void;
  /** Switch the active category/provider chip. Also clears search query. */
  setProvider: (provider: ProviderValue) => void;
  /** Update the list-title input (used by the autocomplete field). */
  setListInput: (value: string) => void;
  /** Reset all state back to initial (call on sheet close or after submit). */
  reset: () => void;
  /**
   * A3 — Resets only the target + listInput, keeping pendingItems in the queue.
   * Call this when the user wants to submit the same items to a second list.
   */
  resetTarget: () => void;
  /** Execute the submit flow for the current state. */
  submit: () => Promise<void>;
  /**
   * Pre-fill the target from the last-used list stored in localStorage.
   * Call this when the sheet opens if no target is already set.
   */
  restoreLastUsedList: () => void;
  /**
   * Save current pending items + target to sessionStorage as a draft.
   * Call when the sheet is closed with pending items present.
   */
  saveDraft: () => void;
  /**
   * Restore a draft from sessionStorage (if any) and clear it.
   * Call when the sheet opens before restoring the last-used list.
   */
  restoreDraft: () => boolean;

  // ── Derived ──────────────────────────────────────────────
  /** Whether a given item key is already in the pending queue. */
  isPending: (key: string) => boolean;
  /** Label for the sticky CTA button. */
  ctaLabel: string;
  /** Whether the CTA button should be enabled. */
  canSubmit: boolean;
  /** Whether a draft exists in sessionStorage (for badge indicator on FAB). */
  hasDraft: boolean;
}

export function useUnifiedAdd(): UseUnifiedAddReturn {
  const router = useRouter();

  // ── Core state ────────────────────────────────────────────────────────────

  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [target, setTargetState] = useState<CollectionTarget>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderValue>('movie');
  const [listInput, setListInputState] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Tracks whether a draft exists in sessionStorage (drives badge on FAB)
  const [hasDraft, setHasDraft] = useState<boolean>(() => !!readDraft());
  // A3 — destination name of the last successful submit (clears on full reset)
  const [lastSubmitDestination, setLastSubmitDestination] = useState<string | null>(null);

  // ── Derived ───────────────────────────────────────────────────────────────

  const stagedKeys = useMemo(() => new Set(pendingItems.map((i) => i.key)), [pendingItems]);
  const isPending = useCallback((key: string) => stagedKeys.has(key), [stagedKeys]);
  const count = pendingItems.length;
  const isNew = target?._id === 'NEW';
  const hasItems = count > 0;

  const ctaLabel = useMemo(() => {
    if (!hasItems && !target) return 'Add something above';
    if (!hasItems && isNew && target) return `Create "${target.title}" (empty)`;
    if (!hasItems) return 'Add something above';
    if (!target) return `Save ${count} item${count !== 1 ? 's' : ''} to Library`;
    if (isNew) return `Create "${target.title}" with ${count} item${count !== 1 ? 's' : ''}`;
    return `Add ${count} item${count !== 1 ? 's' : ''} to ${target.title}`;
  }, [hasItems, target, isNew, count]);

  const canSubmit = hasItems || (isNew && !!target?.title);

  // ── Actions ───────────────────────────────────────────────────────────────

  const addPendingItem = useCallback((item: PendingItem) => {
    setPendingItems((prev) => {
      if (prev.some((i) => i.key === item.key)) return prev; // dedup
      return [...prev, item];
    });
  }, []);

  const removePendingItem = useCallback((key: string) => {
    setPendingItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const setTarget = useCallback((t: CollectionTarget) => {
    setTargetState(t);
  }, []);

  const restoreLastUsedList = useCallback(() => {
    // Only restore when no target is already selected
    setTargetState((prev) => {
      if (prev !== null) return prev;
      const saved = readLastUsedList();
      if (saved) {
        // Mirror the listInput field so the autocomplete input shows the name
        setListInputState(saved.title);
        return saved;
      }
      return null;
    });
  }, []);

  const saveDraft = useCallback(() => {
    // Only persist if there are actual items to resume
    if (pendingItems.length > 0) {
      writeDraft({ pendingItems, target, listInput });
      setHasDraft(true);
    }
  }, [pendingItems, target, listInput]);

  const restoreDraft = useCallback((): boolean => {
    const draft = readDraft();
    if (!draft || draft.pendingItems.length === 0) return false;
    clearDraft();
    setHasDraft(false);
    setPendingItems(draft.pendingItems);
    setTargetState(draft.target);
    setListInputState(draft.listInput);
    return true;
  }, []);

  const setProvider = useCallback((provider: ProviderValue) => {
    setSelectedProvider(provider);
    // Caller is responsible for clearing its own search query state.
  }, []);

  const setListInput = useCallback((value: string) => {
    setListInputState(value);
  }, []);

  const reset = useCallback(() => {
    setPendingItems([]);
    setTargetState(null);
    setSelectedProvider('movie');
    setListInputState('');
    setIsSubmitting(false);
    setLastSubmitDestination(null);
  }, []);

  /**
   * A3 — Resets only the target + listInput so the user can submit the same
   * staged items to a second list. Items and provider selection are preserved.
   */
  const resetTarget = useCallback(() => {
    setTargetState(null);
    setListInputState('');
    setLastSubmitDestination(null);
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────

  const submit = useCallback(async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);

    try {
      // ── Step 1: Resolve the target collection ID ─────────────────────────

      let collectionId: string | null = null;

      if (target?._id === 'NEW') {
        // Path A — create a brand new named collection
        const { collection } = await createCollection({
          title: target.title,
          visibility: 'public',
          isOrdered: false,
        });
        collectionId = collection._id;
      } else if (target?._id) {
        // Path B — use existing collection
        collectionId = target._id;
      }
      // Path C — collectionId stays null → library only (no collection link)

      // ── Step 2: Create each pending item and optionally link to collection ─

      if (pendingItems.length > 0) {
        await Promise.all(
          pendingItems.map(async (pending) => {
            // Build the item payload
            const itemPayload: Parameters<typeof createItem>[0] = {
              title: pending.title,
              categoryId: pending.categoryId,
              scope: 'private',
            };

            // Attach enrichment data for API-sourced items
            if (pending.searchResult && pending.apiProvider) {
              if (pending.searchResult.thumbnail) {
                itemPayload.thumbnail = pending.searchResult.thumbnail;
              }
              if (pending.searchResult.properties?.length) {
                itemPayload.properties = pending.searchResult.properties as any;
              }
              // sourceRef enables dedup + repopulate
              itemPayload.sourceRef = {
                provider: pending.apiProvider,
                externalId: pending.searchResult.externalId,
              };
              if (pending.mediaType) {
                itemPayload.mediaType = pending.mediaType;
              }
            }

            const { item } = await createItem(itemPayload);

            // Link to collection if one was resolved; swallow 409 (already in collection)
            if (collectionId) {
              try {
                await addCollectionItem(collectionId, { itemId: item._id });
              } catch (err) {
                if (!(err instanceof Error && err.message.includes('already exists'))) throw err;
              }
            }
          })
        );
      }

      // ── Step 3: Success toast + optional navigation ──────────────────────

      // Clear any saved draft on successful submit
      clearDraft();
      setHasDraft(false);

      // Persist the successfully used collection for next-open pre-fill.
      if (target && collectionId && target._id !== 'NEW') {
        writeLastUsedList(target);
      } else if (target?._id === 'NEW' && collectionId) {
        // After creation, persist the newly created list so the next open
        // pre-fills it as an existing collection.
        writeLastUsedList({ _id: collectionId, title: target.title });
      }

      // Collect unique categoryIds from the saved items so we can match circles.
      const savedCategoryIds = new Set(pendingItems.map((p) => p.categoryId));

      // Fetch circles in the background (non-blocking — degrade gracefully).
      // We match circles whose searchDefaults.categoryId is in the saved set,
      // or circles with no category filter (= they surface all categories).
      let circleHints: string[] = [];
      try {
        const { circles } = await fetchCircles();
        circleHints = circles
          .filter((c) =>
            !c.searchDefaults.categoryId ||
            savedCategoryIds.has(c.searchDefaults.categoryId)
          )
          .slice(0, 3)
          .map((c) => c.name);
      } catch {
        // Non-critical — swallow and show a plain success toast
      }

      // Determine human-readable destination name for toast + A3 multi-list prompt
      const destName =
        target?._id === 'NEW' ? target.title
        : target ? target.title
        : 'Library';

      if (target?._id === 'NEW' && collectionId) {
        const newId = collectionId;
        toast.success(`"${target.title}" created`, {
          description:
            circleHints.length > 0
              ? `Your picks will appear in: ${circleHints.join(', ')}`
              : undefined,
          action: {
            label: 'View list →',
            onClick: () => router.push(`/lists/${newId}`),
          },
        });
      } else if (pendingItems.length > 0) {
        const dest = target ? target.title : 'Library';
        const count = pendingItems.length;
        toast.success(
          `${count} item${count !== 1 ? 's' : ''} saved to ${dest}`,
          {
            description:
              circleHints.length > 0
                ? `Your ${count === 1 ? 'pick' : 'picks'} will appear in: ${circleHints.join(', ')}`
                : undefined,
            action: collectionId
              ? {
                  label: 'View list →',
                  onClick: () => router.push(`/lists/${collectionId}`),
                }
              : undefined,
          }
        );
      } else {
        // Empty new list created
        if (collectionId && target) {
          const newId = collectionId;
          toast.success(`"${target.title}" created`, {
            action: {
              label: 'View list →',
              onClick: () => router.push(`/lists/${newId}`),
            },
          });
        }
      }

      // A3 — After submit, keep items in the queue so the user can add them to
      // a second list. Reset only the target. The sheet stays open, showing the
      // "Add to another list?" prompt. Full reset happens on sheet close.
      setLastSubmitDestination(destName);
      setTargetState(null);
      setListInputState('');
      router.refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, target, pendingItems, reset, router]);

  return {
    pendingItems,
    target,
    selectedProvider,
    listInput,
    isSubmitting,
    lastSubmitDestination,
    addPendingItem,
    removePendingItem,
    setTarget,
    setProvider,
    setListInput,
    reset,
    resetTarget,
    submit,
    restoreLastUsedList,
    saveDraft,
    restoreDraft,
    hasDraft,
    isPending,
    ctaLabel,
    canSubmit,
  };
}
